"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { normalizeRoutePathname } from "@/lib/utils/route-pathname";
import { requestInvocaPNAPIRun } from "@/lib/utils/invoca/pnapi-run";

/**
 * The class Invoca's tag targets for number swapping - the same literal the
 * base studio Button adds to tel: links (see app/components/studio/Button).
 */
const INVOCA_CLASS = "InvocaNumber";

/**
 * /smile pages hide the app Header and author their own chrome in Contentful,
 * which is where the untagged tel: container-links live. Scope to exactly the
 * /smile section so the rest of the site (already fully tagged via Button)
 * never pays for the scan. The /bb door and the /composable/ prefix are route
 * aliases onto the same smile experiences (lib/utils/route-pathname.ts), so
 * /bb/smile/dentures/ and /composable/smile/dentures/ normalize to
 * /smile/dentures/ here and keep the safety net on both alias seams too.
 */
const isSmilePath = (rawPathname: string | null): boolean => {
  if (!rawPathname) return false;
  const pathname = normalizeRoutePathname(rawPathname);
  return pathname === "/smile" || pathname.startsWith("/smile/");
};

// Same client-side studio detection as CrestaChat: never mutate the DOM while
// an author is editing inside the Contentful studio iframe.
const isStudioEditorMode = () => globalThis.window !== undefined && new URLSearchParams(globalThis.location.search).get("expEditorMode") === "true";

/**
 * Safety net for Invoca call tracking on /smile pages.
 *
 * Tel: links built with the studio Button/InvocaButton components already
 * carry the `InvocaNumber` class, but plain Contentful container-links
 * (a container with a tel: hyperlink - the /smile header phone row and the
 * "Call to Book" CTAs) render as bare anchors that Invoca's tag cannot
 * target, so those calls dial the static number with no DNI attribution.
 * Editing the locked /smile header content is off the table, so instead this
 * component tags every untagged tel: anchor after hydration and re-runs
 * `Invoca.PNAPI.run()` so the swap picks them up.
 *
 * A MutationObserver keeps the guarantee through client-side re-renders
 * (office selection re-runs placeholder replacement and remounts experience
 * content) and covers any future authoring misses. Adding a class an anchor
 * already has is a no-op, and the scan only acts on anchors still missing the
 * class, so the observer converges instead of looping: our own class writes
 * trigger one follow-up scan that finds nothing to do.
 */
const SmileInvocaTelAutoTag = () => {
  const $ = _c(5);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = isSmilePath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const enabled = t0;
  let t1;
  let t2;
  if ($[2] !== enabled) {
    t1 = () => {
      if (!enabled || isStudioEditorMode()) {
        return;
      }
      let scanQueued = false;
      const tagUntaggedTelLinks = _temp;
      const queueScan = () => {
        if (scanQueued) {
          return;
        }
        scanQueued = true;
        queueMicrotask(() => {
          scanQueued = false;
          tagUntaggedTelLinks();
        });
      };
      tagUntaggedTelLinks();
      const observer = new MutationObserver(queueScan);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "href"]
      });
      return () => {
        observer.disconnect();
      };
    };
    t2 = [enabled];
    $[2] = enabled;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
};
export default SmileInvocaTelAutoTag;
function _temp() {
  const untagged = document.querySelectorAll(`a[href^="tel:" i]:not(.${INVOCA_CLASS})`);
  if (untagged.length === 0) {
    return;
  }
  for (const anchor of untagged) {
    anchor.classList.add(INVOCA_CLASS);
  }
  requestInvocaPNAPIRun();
}